import type { ConfiguratorPageDefinition, StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import * as RailCstic from "./TKDMRailsCstics";
import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { BASE_DESIGNATION_SEPARATOR } from "~/Configurators/Linear/constants";
import { type LinearConfiguration, type LinearCstic, type LinearSubstep } from "~/Configurators/Linear/models";
import { getHighestVisitedStep } from "~/Configurators/Linear/redux/selectors";
import { sendRailAddToCart } from "~/Configurators/Rail/api/sendRailAddToCart";
import { sendRailUpdate } from "~/Configurators/Rail/api/sendRailConfiguratorUpdate";
import { sendRailResetConfigurator } from "~/Configurators/Rail/api/sendRailResetConfigurator";
import { sendRailMiscChange, sendRailStepChange } from "~/Configurators/Rail/api/sendRailStepChange";
import { sendRailDataReset } from "~/Configurators/Rail/api/sendRailStepReset";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import type { RootState } from "~/redux/store";
import { toMapFn } from "~/utils/reducers";
import { csticHasValue, getCsticsValue, join } from "~/Configurators/common/v2/helper";
import { TKDMRailSummaryData } from "~/Configurators/Rail/v2/configuration/tkdm/summary/TKDMRailSummaryData";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/Configurators/Shaft/shaftGaHelper";

import { getStep1SummaryData, getStep2SummaryData } from "./summary/TKDMSummaryDataProvider";
import type { SummaryData } from "~/Configurators/Shaft/SummaryDataProvider";
import { fetchSimpleSubstepValue } from "~/Configurators/Linear/components/fetchSimpleSubstepValue";
import { grabXmlTranslation } from "~/Configurators/grabExternalTranslation";
import { prepareSummaryDataName, prepareSummaryDataValue } from "~/Configurators/common/v2/gaHelper";
import { validateFromDefinition } from "~/Configurators/common/v2/validation";

const RAIL_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });

      const extraData: SummaryData[] = [RailCstic.GuidewayDesign, RailCstic.Size].map((csticName) => {
        const cstic = kmat.cstics[csticName];
        const csticValue = cstic?.values.find((item) => item.value == cstic?.value);
        return {
          name: summaryDataProvider.t(cstic.label),
          value: summaryDataProvider.t(csticValue?.label ?? ""),
        };
      });

      const seriesValue = grabXmlTranslation(
        summaryDataProvider,
        fetchSimpleSubstepValue(state.linearConfigurator.substeps.Series) ?? "",
      );
      extraData[0].value = seriesValue;

      const selectionData = {
        ...toMapFn(extraData, prepareSummaryDataName, prepareSummaryDataValue),
        ...toMapFn(step1Data, prepareSummaryDataName, prepareSummaryDataValue),
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
  },
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      const selectionData = toMapFn(step2Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
  },
];

export const TKDM_RAIL_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Rail,
  productCode: TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.rail.name",
  },
  apiCalls: {
    update: sendRailUpdate,
    reset: sendRailResetConfigurator,
    stepChange: sendRailStepChange,
    stepReset: sendRailDataReset,
    miscChange: sendRailMiscChange,
    addToCart: sendRailAddToCart,
  },
  summary: TKDMRailSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);
    return step >= 1 && hasLengthSet;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);

    return step >= 1 && hasLengthSet;
  },
  stepHardcodedData: RAIL_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    return [RailCstic.GuidewayLength, RailCstic.AL, RailCstic.AR].includes(csticName);
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [series, sizeCode, version, guidewayDesign] = getCsticsValue(
      kmat,
      RailCstic.DesignType_Series,
      RailCstic.DesignType_SizeCode,
      RailCstic.DesignType_Version,
      RailCstic.DesignType_GuidewayDesign,
    );

    return join("-", join("", series, sizeCode), version, guidewayDesign) || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kuem-rail",
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    const alCstic = cstics[RailCstic.AL];
    if (alCstic) {
      alCstic.label = "configurator.rail.section.al.name";
    }

    const arCstic = cstics[RailCstic.AR];
    if (arCstic) {
      arCstic.label = "configurator.rail.section.ar.name";
    }

    const lengthCstic = cstics[RailCstic.GuidewayLength];
    if (lengthCstic) {
      lengthCstic.label = "configurator.rail.cstic.guidewayLength.name";
    }

    cstics[RailCstic.PartSizing] = {
      ...EMPTY_FAKE_CSTIC,
      label: "configurator.rail.cstic.partSizing.name",
    };

    cstics[RailCstic.PartAmount] = {
      ...EMPTY_FAKE_CSTIC,
      label: "configurator.rail.cstic.partAmount.name",
      value: "1.0",
      values: [
        {
          key: "1.0",
          info: "",
          imageUrl: "",
          value: "1.0",
          label: "configurator.rail.cstic.partAmount.1.0.name",
        },
      ],
    };

    return model;
  },
  postDefinitionProcessor: (model, dto) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
  canQueryAvailability: (state) => {
    const cstics = getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;

    const hasAllRequiredCstics = [RailCstic.GuidewayDesign, RailCstic.Size, RailCstic.Accuracy].every(
      (cstic) => !!cstics[cstic]?.value,
    );

    return hasAllRequiredCstics;
  },
} as const;

const EMPTY_FAKE_CSTIC: LinearCstic = {
  key: "",
  name: "",
  label: "",
  value: "",
  groupId: "",
  values: [],
  type: "",
  info: "",
  description: "",
  readonly: false,
  fake: true,
};

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const kmats = model.kmats;
  const conflicts = "conflictedCstics" in model ? model.conflictedCstics : [];

  const canVisitPage2 =
    model.steps[0].substeps?.every(
      (substep) => validateFromDefinition(model.substeps[substep] as LinearSubstep, kmats, conflicts).substepValid,
    ) ?? false;

  const canVisitPage3 =
    model.steps[1].substeps?.every(
      (substep) => validateFromDefinition(model.substeps[substep] as LinearSubstep, kmats, conflicts).substepValid,
    ) ?? false;

  const allowedSteps = [
    [true, canVisitPage2, canVisitPage3],
    [true, true, canVisitPage3],
    [true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(TKDM_RAIL_DEFINITION);
